const ROOT = "/marketing/product-story/v2";

const storyAsset = ({ id, width, height, alt, widths }) => Object.freeze({
  id,
  width,
  height,
  alt,
  variants: Object.freeze(widths.map((variantWidth) => Object.freeze({
    width: variantWidth,
    avif: `${ROOT}/${id}.w${variantWidth}.avif`,
    webp: `${ROOT}/${id}.w${variantWidth}.webp`,
  }))),
});

const STANDARD_WIDTHS = Object.freeze([480, 640, 768, 960, 1280]);

const PRODUCT_STORY_ASSETS = Object.freeze({
  overview: storyAsset({
    id: "01-unified-erp",
    width: 1586,
    height: 992,
    widths: [...STANDARD_WIDTHS, 1586],
    alt: "Vidyapeeth360 school-day overview showing connected admissions, academics, finance, staffing, transport, tasks and reports in a sample workspace.",
  }),
  academics: storyAsset({
    id: "02-academics",
    width: 1536,
    height: 1024,
    widths: [...STANDARD_WIDTHS, 1536],
    alt: "Vidyapeeth360 academic workspace showing timetable, curriculum progress, attendance, assignments, examinations and report-card approval.",
  }),
  finance: storyAsset({
    id: "03-fee-finance",
    width: 1568,
    height: 1003,
    widths: [...STANDARD_WIDTHS, 1568],
    alt: "Vidyapeeth360 fee and finance workspace showing invoices, collections, overdue review, concessions, refunds and provider evidence.",
  }),
  admissions: storyAsset({
    id: "05-admissions",
    width: 1586,
    height: 992,
    widths: [...STANDARD_WIDTHS, 1586],
    alt: "Vidyapeeth360 admissions workspace showing enquiry stages, application review, documents and supervised student-record creation.",
  }),
  family: storyAsset({
    id: "07-parent-student",
    width: 1568,
    height: 1003,
    widths: [...STANDARD_WIDTHS, 1568],
    alt: "Vidyapeeth360 family portal showing attendance, the daily timetable, invoices, homework, results and notices.",
  }),
  communication: storyAsset({
    id: "09-communication",
    width: 1586,
    height: 992,
    widths: [...STANDARD_WIDTHS, 1586],
    alt: "Vidyapeeth360 communication centre showing audience review, moderated conversations, recent notices and delivery evidence.",
  }),
  analytics: storyAsset({
    id: "10-analytics-reports",
    width: 1586,
    height: 992,
    widths: [...STANDARD_WIDTHS, 1586],
    alt: "Vidyapeeth360 analytics and reports workspace showing attendance, fee, admissions and exam indicators with governed exports.",
  }),
});

const PRODUCT_STORY_CROPS = Object.freeze({
  assignedWork: storyAsset({
    id: "01-unified-erp.assigned-work.crop",
    width: 280,
    height: 417,
    widths: [280],
    alt: "Assigned work panel from the Vidyapeeth360 school-day walkthrough, with four review tasks and their due state.",
  }),
  curriculum: storyAsset({
    id: "02-academics.curriculum-progress.crop",
    width: 545,
    height: 440,
    widths: [320, 480, 545],
    alt: "Curriculum progress detail from the Vidyapeeth360 academic walkthrough, with subject-level planned and completed progress.",
  }),
  providerEvidence: storyAsset({
    id: "03-fee-finance.provider-evidence.crop",
    width: 704,
    height: 259,
    widths: [320, 480, 640, 704],
    alt: "Collection provider-evidence detail from the Vidyapeeth360 fee and finance walkthrough.",
  }),
  overdueReview: storyAsset({
    id: "03-fee-finance.overdue-review.crop",
    width: 637,
    height: 259,
    widths: [320, 480, 637],
    alt: "Overdue invoice review detail from the Vidyapeeth360 fee and finance walkthrough.",
  }),
  admissionsPipeline: storyAsset({
    id: "05-admissions.admissions-pipeline.crop",
    width: 1019,
    height: 417,
    widths: [320, 480, 640, 960, 1019],
    alt: "Admissions pipeline detail from the Vidyapeeth360 admissions walkthrough, showing six supervised enquiry stages.",
  }),
  familyAttendance: storyAsset({
    id: "07-parent-student.family-attendance.crop",
    width: 387,
    height: 302,
    widths: [320, 387],
    alt: "Family attendance detail from the Vidyapeeth360 family-portal walkthrough.",
  }),
  moderatedChannel: storyAsset({
    id: "09-communication.moderated-channel.crop",
    width: 750,
    height: 539,
    widths: [320, 480, 640, 750],
    alt: "Moderated conversation detail from the Vidyapeeth360 communication walkthrough.",
  }),
  crossWorkspaceIndicators: storyAsset({
    id: "10-analytics-reports.cross-workspace-indicators.crop",
    width: 951,
    height: 390,
    widths: [320, 480, 640, 951],
    alt: "Cross-workspace indicators from the Vidyapeeth360 analytics and reports walkthrough.",
  }),
  reportCatalogue: storyAsset({
    id: "10-analytics-reports.report-catalogue.crop",
    width: 954,
    height: 443,
    widths: [320, 480, 640, 954],
    alt: "Report catalogue detail from the Vidyapeeth360 analytics and reports walkthrough.",
  }),
});

const panel = (image, mobileImage, label, title, description, to) => Object.freeze({
  image,
  mobileImage,
  label,
  title,
  description,
  to,
});

export const SOLUTION_PRODUCT_EVIDENCE = Object.freeze({
  school: Object.freeze({
    eyebrow: "A connected school day",
    title: "See the whole operating day without stitching together disconnected tabs.",
    description: "Move from the executive view into academics and finance while keeping the same workspace, role boundaries and review trail.",
    qualities: Object.freeze(["Cross-workspace view", "Role-aware tasks", "Reviewable hand-offs"]),
    panels: Object.freeze([
      panel(PRODUCT_STORY_ASSETS.overview, PRODUCT_STORY_CROPS.assignedWork, "School-day overview", "One command view for daily operations", "Admissions, classes, collections, staffing, transport, tasks and reports stay visible in one role-aware operating surface.", "/modules/dashboard-analytics"),
      panel(PRODUCT_STORY_ASSETS.academics, PRODUCT_STORY_CROPS.curriculum, "Academic workspace", "Plan, teach, review and publish in sequence", "Timetable, curriculum, attendance, assignments, examinations and report-card approval share one connected academic workflow.", "/modules/timetable-management"),
      panel(PRODUCT_STORY_ASSETS.finance, PRODUCT_STORY_CROPS.providerEvidence, "Fee & finance", "Collections with the evidence beside them", "Invoice state, payment evidence, overdue review, concessions, refunds and accounting outputs remain clearly separated and reviewable.", "/modules/fee-collection"),
    ]),
  }),
  preschool: Object.freeze({
    eyebrow: "A calmer family experience",
    title: "Give families a clear front door while staff keep one reliable record.",
    description: "Connect guardian context, the daily child view and approved communication without exposing internal operating complexity to families.",
    qualities: Object.freeze(["Guardian-linked", "Care permissions", "Approved updates"]),
    panels: Object.freeze([
      panel(PRODUCT_STORY_ASSETS.family, PRODUCT_STORY_CROPS.familyAttendance, "Family portal", "The day families need, in one place", "Attendance, the daily schedule, invoices, learning updates, results and notices are organised around the child and guardian context.", "/modules/parent-app"),
      panel(PRODUCT_STORY_ASSETS.admissions, PRODUCT_STORY_CROPS.admissionsPipeline, "Admissions", "Carry guardian context into enrolment", "Enquiries, document review and supervised record creation make the hand-off from first conversation to enrolled child visible.", "/modules/student-admission"),
      panel(PRODUCT_STORY_ASSETS.communication, PRODUCT_STORY_CROPS.moderatedChannel, "Communication", "Review the audience before an update leaves", "Notices, moderated conversations and channel evidence help teams communicate deliberately through configured providers.", "/modules/parent-notification"),
    ]),
  }),
  college: Object.freeze({
    eyebrow: "Concrete surfaces for the fit review",
    title: "Map programme operations against workflow views your team can inspect together.",
    description: "Use the analytics, academic and finance walkthroughs to identify what maps cleanly, what needs configuration and what belongs in the proposal scope.",
    qualities: Object.freeze(["Programme mapping", "Fit-led scope", "Explicit boundaries"]),
    panels: Object.freeze([
      panel(PRODUCT_STORY_ASSETS.analytics, PRODUCT_STORY_CROPS.crossWorkspaceIndicators, "Analytics & reports", "A shared review surface for operational indicators", "Attendance, fee, admissions and assessment views create a concrete starting point for programme, cohort and reporting requirements.", "/modules/dashboard-analytics"),
      panel(PRODUCT_STORY_ASSETS.academics, PRODUCT_STORY_CROPS.curriculum, "Academic operations", "Test the academic model with real workflow states", "Scheduling, curriculum progress, attendance, examinations and publication states can be reviewed against the institution's regulations.", "/modules/timetable-management"),
      panel(PRODUCT_STORY_ASSETS.finance, PRODUCT_STORY_CROPS.providerEvidence, "Finance boundaries", "Separate product records from external settlement", "Fee structures, collections, overdue review and accounting outputs remain explicit, including the provider and reconciliation boundaries.", "/modules/fee-collection"),
    ]),
  }),
  coaching: Object.freeze({
    eyebrow: "From enquiry to the next class",
    title: "Make the centre's operating loop visible before expanding it.",
    description: "Review enquiry movement, collections and cross-workspace indicators in one coherent product story, then release the accepted model centre by centre.",
    qualities: Object.freeze(["Assigned follow-up", "Centre-by-centre", "Provider-aware"]),
    panels: Object.freeze([
      panel(PRODUCT_STORY_ASSETS.admissions, PRODUCT_STORY_CROPS.admissionsPipeline, "Enquiry pipeline", "Give every enquiry a visible next step", "Assignment, follow-up, application, conversion and documents stay in a supervised pipeline instead of separate centre spreadsheets.", "/modules/enquiry-management"),
      panel(PRODUCT_STORY_ASSETS.finance, PRODUCT_STORY_CROPS.overdueReview, "Instalments & dues", "Review collection state with its supporting evidence", "Invoices, partial collections, overdue items, concessions and refunds can be evaluated against the institute's actual fee scenarios.", "/modules/fee-collection"),
      panel(PRODUCT_STORY_ASSETS.analytics, PRODUCT_STORY_CROPS.reportCatalogue, "Centre review", "Bring the operating signals into one review", "Attendance, fee, admissions and assessment indicators provide a concrete base for branch ownership and reporting requirements.", "/modules/dashboard-analytics"),
    ]),
  }),
});

export function solutionProductEvidenceFor(key) {
  return SOLUTION_PRODUCT_EVIDENCE[key] || null;
}

export { PRODUCT_STORY_ASSETS, PRODUCT_STORY_CROPS };
